import React from "react";

// ---------------------- Fixed JSON Data for CMPT410 - Machine Learning ----------------------
const currentData = [
  {
    section: "001",
    enrolled: 25,
    capacity: 25,
    wait: 9, // allowed because section is full
    instructor: "Dr. Smith",
    roomCapacity: 25,
    campus: "Burnaby",
    modality: "In-Person",
    status: "Active",
  },
  {
    section: "002",
    enrolled: 25,
    capacity: 25,
    wait: 7, // allowed because section is full
    instructor: "Dr. Johnson",
    roomCapacity: 25,
    campus: "Surrey",
    modality: "Hybrid",
    status: "Active",
  },
];

const historicalData = [
  {
    term: "Fall 2024",
    sections: [
      {
        details: "001 Section (In-Person, Burnaby Campus, Morning classes)",
        enrolled: 25,
        capacity: 25,
        campus: "Burnaby",
        waitlist: 7, // full section → waitlist allowed
      },
      {
        details: "002 Section (Hybrid, Surrey Campus, Afternoon classes)",
        enrolled: 23, // not full, so waitlist forced to 0
        capacity: 25,
        campus: "Surrey",
        waitlist: 0,
      },
    ],
  },
  {
    term: "Spring 2024",
    sections: [
      {
        details: "001 Section (In-Person, Burnaby Campus, Morning classes)",
        enrolled: 25, // not full → waitlist forced to 0 (15/25)
        capacity: 25,
        campus: "Burnaby",
        waitlist: 4,
      },
      {
        details: "002 Section (Online, Surrey Campus, Evening classes)",
        enrolled: 25, // full
        capacity: 25,
        campus: "Surrey",
        waitlist: 2,
      },
    ],
  },
  {
    term: "Fall 2023",
    sections: [
      {
        details: "001 Section (In-Person, Burnaby Campus, Morning classes)",
        enrolled: 23, // not full → waitlist forced to 0
        capacity: 25,
        campus: "Burnaby",
        waitlist: 0,
      },
      {
        details: "002 Section (Hybrid, Surrey Campus, Afternoon classes)",
        enrolled: 25, // full
        capacity: 25,
        campus: "Surrey",
        waitlist: 3,
      },
    ],
  },
  {
    term: "Spring 2023",
    sections: [
      {
        details: "001 Section (In-Person, Burnaby Campus, Morning classes)",
        enrolled: 22, // not full → waitlist forced to 0
        capacity: 25,
        campus: "Burnaby",
        waitlist: 0,
      },
      {
        details: "002 Section (Online, Surrey Campus, Evening classes)",
        enrolled: 25, // full
        capacity: 25,
        campus: "Surrey",
        waitlist: 2,
      },
    ],
  },
  {
    term: "Fall 2022",
    sections: [
      {
        details: "001 Section (In-Person, Burnaby Campus, Morning classes)",
        enrolled: 24,
        capacity: 25,
        campus: "Burnaby",
        waitlist: 0,
      },
      {
        details: "002 Section (Hybrid, Surrey Campus, Afternoon classes)",
        enrolled: 19,
        capacity: 25,
        campus: "Surrey",
        waitlist: 0,
      },
    ],
  },
  {
    term: "Spring 2022",
    sections: [
      {
        details: "001 Section (In-Person, Burnaby Campus, Morning classes)",
        enrolled: 25,
        capacity: 25,
        campus: "Burnaby",
        waitlist: 1,
      },
      {
        details: "002 Section (Online, Surrey Campus, Evening classes)",
        enrolled: 22,
        capacity: 25,
        campus: "Surrey",
        waitlist: 0,
      },
    ],
  },
  {
    term: "Fall 2021",
    sections: [
      {
        details: "001 Section (In-Person, Burnaby Campus, Morning classes)",
        enrolled: 24,
        capacity: 25,
        campus: "Burnaby",
        waitlist: 0,
      },
      {
        details: "002 Section (Hybrid, Surrey Campus, Afternoon classes)",
        enrolled: 22,
        capacity: 25,
        campus: "Surrey",
        waitlist: 0,
      },
    ],
  },
  {
    term: "Spring 2021",
    sections: [
      {
        details: "001 Section (In-Person, Burnaby Campus, Morning classes)",
        enrolled: 17,
        capacity: 25,
        campus: "Burnaby",
        waitlist: 0,
      },
      {
        details: "002 Section (Online, Surrey Campus, Evening classes)",
        enrolled: 23,
        capacity: 25,
        campus: "Surrey",
        waitlist: 0,
      },
    ],
  },
];

// ---------------------- Main Component ----------------------
const SectionDetails = () => {
  // Using the static data directly (sorting disabled)
  const sortedCurrentEnrollment = currentData;

  return (
    <div className="max-h-[80vh] overflow-y-auto space-y-4">
      {/* ================== AI Insights Section ================== */}
      <div className="bg-white rounded-lg border border-gray-300 px-3 py-2">
        <h2 className="text-xl text-gray-700 font-semibold mb-2">Overall Insights</h2>
        <ul className="list-disc text-gray-600 list-inside ml-4 mb-1">
          <li>
            In the last 2 like terms, demand for online and hybrid sections was higher than other modalities.
          </li>
          <li>
            In the last 3 like terms, there has been an increase in students from the Computer Science (CMPT) program taking this course to meet the Upper Division Elective Requirement.
          </li>
          <li>
            Usually, this course overlaps with CMPT412 - Computer Vision (On Burnaby and Surrey Campuses).
          </li>
        </ul>
      </div>

      {/* ================== Current Enrollment Table ================== */}
      <div className="bg-white rounded-lg border border-gray-300 px-3 py-2">
        <h3 className="text-lg text-gray-700 font-semibold mb-2">Current Enrollment</h3>
        <div className="overflow-x-auto relative">
          <table className="table-fixed border border-gray-300 w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2">Section</th>
                <th className="px-4 py-2">Enrl/Cap</th>
                <th className="px-4 py-2">Waitlist</th>
                <th className="px-4 py-2">Instructor</th>
                <th className="px-4 py-2">Room Cap</th>
                <th className="px-4 py-2">Campus</th>
                <th className="px-4 py-2">Modality</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {sortedCurrentEnrollment.map((section, idx) => (
                <tr
                  key={idx}
                  className="bg-white border-b hover:bg-gray-50 whitespace-nowrap"
                >
                  <td className="px-4 py-2">{section.section}</td>
                  <td className="px-4 py-2">
                    {section.enrolled}/{section.capacity}
                  </td>
                  <td className="px-4 py-2">{section.wait}</td>
                  <td className="px-4 py-2">{section.instructor}</td>
                  <td className="px-4 py-2">{section.roomCapacity} seats</td>
                  <td className="px-4 py-2">{section.campus}</td>
                  <td className="px-4 py-2">{section.modality}</td>
                  <td className="px-4 py-2">{section.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================== Historical Enrollment Table ================== */}
      <div className="bg-white rounded-lg border border-gray-300 px-3 py-2">
        <h3 className="text-lg text-gray-700 font-semibold mb-2">
          Historical / Upcoming Terms Enrollment
        </h3>
        <div className="overflow-x-auto relative">
          <table className="w-full border border-gray-300 text-left text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100">
              <tr>
                <th className="px-4 py-2">Term</th>
                <th className="px-2 py-2">Course/Section Details</th>
                <th className="px-2 py-2">Campus</th>
                <th className="px-2 py-2">Enrollment Bar</th>
                <th className="px-2 py-2">Fill Rate</th>
                <th className="px-2 py-2">Waitlist</th>
              </tr>
            </thead>
            <tbody>
              {historicalData.map((termData) => {
                const { term, sections } = termData;
                return sections.map((section, sectionIdx) => {
                  const fillRate = Math.round(
                    (section.enrolled / section.capacity) * 100
                  );
                  let barColor = "bg-green-500";
                  if (fillRate >= 80 && fillRate <= 95) {
                    barColor = "bg-yellow-500";
                  } else if (fillRate > 95) {
                    barColor = "bg-red-500";
                  }
                  const showTermCell = sectionIdx === 0;
                  const isLastSubrow = sectionIdx === sections.length - 1;
                  const rowClasses = isLastSubrow
                    ? "bg-white border-b hover:bg-gray-50 whitespace-nowrap"
                    : "bg-white border-b-0 hover:bg-gray-50 whitespace-nowrap";
                  return (
                    <tr key={sectionIdx} className={rowClasses}>
                      {showTermCell && (
                        <td
                          rowSpan={sections.length}
                          className="px-4 py-2 font-semibold text-sm"
                        >
                          {term}
                        </td>
                      )}
                      <td className="px-2 py-1 text-xs">
                        <div className="text-gray-700">{section.details}</div>
                      </td>
                      <td className="px-2 py-1 text-xs">{section.campus}</td>
                      <td className="px-2 py-1 text-xs">
                        <div className="w-full bg-gray-200 mt-1 rounded-full h-2">
                          <div
                            className={`${barColor} h-2 rounded-full`}
                            style={{ width: `${fillRate}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-2 py-1 text-xs font-medium text-gray-700">
                        {section.enrolled}/{section.capacity} Seats ({fillRate}%)
                      </td>
                      <td className="px-2 py-1 text-xs">{section.waitlist}</td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SectionDetails;
